import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PlatformContactsService, PlatformOwner } from '../../services/platform-contacts.service';
import { ManagementRecordCardComponent } from '../ui/management-record-card.component';
import { NotificationService } from '../../services/notification.service';
import { DialogService } from '../../services/dialog.service';
import { friendlyError } from '../../utils/friendly-error';

@Component({
  selector: 'app-platform-contacts-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, ManagementRecordCardComponent],
  template: `
    <section class="contacts-admin" *ngIf="!loading(); else loadingState">
      <header class="contacts-head">
        <div>
          <span class="eyebrow">CONFIGURACIÓN DE PLATAFORMA</span>
          <h3>Personal administrativo de TuSala</h3>
          <p>Define los correos y teléfonos oficiales, el remitente de los correos y los dueños delegados del sistema. Aparecen en todos los frentes, correos, documentos y comprobantes.</p>
        </div>
      </header>

      <!-- Correos -->
      <article class="contacts-card contacts-emails" id="emails-section">
        <header><i class="bi bi-envelope"></i><div><small>CORREOS OFICIALES</small><strong>Soporte, administración y remitente</strong></div></header>
        <div class="contacts-email-fields">
          <label class="field-block">
            <span>Correo de soporte <em class="req">*</em></span>
            <input type="email" id="support-email-input" maxlength="120" [(ngModel)]="supportEmail" placeholder="soporte@tusala.net" [class.invalid]="emailInvalid(supportEmail())" />
          </label>
          <label class="field-block">
            <span>Correo de administración <em class="req">*</em></span>
            <input type="email" id="administration-email-input" maxlength="120" [(ngModel)]="administrationEmail" placeholder="administracion@tusala.com" [class.invalid]="emailInvalid(administrationEmail())" />
          </label>
          <label class="field-block">
            <span>Correo que envía los correos del sistema <em class="req">*</em></span>
            <input type="email" id="sender-email-input" maxlength="120" [(ngModel)]="senderEmail" placeholder="notificacion@tusala.net" [class.invalid]="emailInvalid(senderEmail())" />
          </label>
        </div>
        <p *ngIf="emailsError()" class="contacts-error" role="alert">{{ emailsError() }}</p>
      </article>

      <!-- Dueños -->
      <article class="contacts-card contacts-directory" id="owners-section" [class.invalid]="!!ownersError()">
        <header>
          <i class="bi bi-person-badge"></i>
          <div><small>RESPONSABLES</small><strong>Dueños de TuSala</strong></div>
          <button type="button" class="add-chip" (click)="openOwnerCreate()"><i class="bi bi-plus-lg"></i> Nuevo dueño</button>
        </header>
        <div class="owner-directory">
          <app-management-record-card
            *ngFor="let owner of owners(); let i = index"
            [tone]="owner.name.trim() ? 'default' : 'inactive'"
            kind="people"
            (recordActivate)="openOwnerEdit(i)"
          >
            <span recordIcon>{{ (owner.name || '?').charAt(0).toUpperCase() }}</span>
            <div recordPrimary><small>DUEÑO DE TUSALA</small><strong>{{ owner.name || 'Sin nombre' }}</strong></div>
            <div recordSummary><small>CORREO</small><strong>{{ owner.email || 'Sin correo' }}</strong><span>{{ owner.email ? 'Registrado' : 'Pendiente' }}</span></div>
            <div recordStatus><small>ROL</small><strong>Superadministrador delegado</strong><span [class.account-state-ok]="owner.has_owner_account" [class.account-state-off]="!owner.has_owner_account">{{ owner.has_owner_account ? (owner.owner_account_active ? 'Cuenta de propietario activa' : 'Cuenta de propietario inactiva') : 'Sin cuenta de propietario' }}</span></div>
            <div recordActions>
              <button type="button" class="icon-action" title="Editar dueño" aria-label="Editar dueño" (click)="openOwnerEdit(i)"><i class="bi bi-pencil"></i></button>
              <button type="button" class="icon-action danger" title="Quitar dueño" aria-label="Quitar dueño" (click)="openOwnerRemove(i)"><i class="bi bi-person-x"></i></button>
            </div>
          </app-management-record-card>
        </div>
        <div class="owner-empty" *ngIf="owners().length === 0">
          <i class="bi bi-person-vcard"></i><strong>Aún no hay dueños registrados</strong>
          <span>Agrega al menos un dueño delegado de TuSala.</span>
        </div>
        <p *ngIf="ownersError()" class="contacts-error" role="alert">{{ ownersError() }}</p>
      </article>

      <!-- Teléfonos -->
      <article class="contacts-card contacts-phones" id="phones-section" [class.invalid]="!!phonesError()">
        <header>
          <i class="bi bi-telephone"></i>
          <div><small>LÍNEAS DE CONTACTO</small><strong>Teléfonos de TuSala</strong></div>
        </header>
        <div class="phone-list">
          <div class="phone-row" *ngFor="let phone of contactPhones(); let i = index">
            <i class="bi bi-telephone-outbound"></i>
            <input type="tel" maxlength="40" [ngModel]="phone" (ngModelChange)="setPhone(i, $event)" placeholder="+503 0000-0000" [class.invalid]="phonesError() && !phone.trim()" />
            <button type="button" class="icon-action danger" *ngIf="contactPhones().length > 1" (click)="removePhone(i)" aria-label="Quitar teléfono"><i class="bi bi-x-lg"></i></button>
          </div>
        </div>
        <button type="button" class="add-chip" (click)="addPhone()"><i class="bi bi-plus-lg"></i> Agregar teléfono</button>
        <p *ngIf="phonesError()" class="contacts-error" role="alert">{{ phonesError() }}</p>
      </article>

      <footer class="contacts-actions">
        <button type="button" class="secondary-btn" (click)="discard()" [disabled]="saving()">Descartar</button>
        <button type="button" class="action-btn" (click)="save()" [disabled]="saving()">
          <i class="bi" [class.bi-send-check]="!saving()" [class.bi-arrow-repeat]="saving()"></i> {{ saving() ? 'Guardando...' : 'Guardar contactos' }}
        </button>
      </footer>
    </section>

    <!-- Modal dueño (crear / editar) -->
    <div *ngIf="ownerModalOpen()" class="modal-overlay" (click)="closeOwnerModal()">
      <section class="modal-box owner-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
        <header class="modal-header">
          <span class="modal-icon"><i class="bi bi-person-badge"></i></span>
          <div><small>{{ editingOwnerIndex() === null ? 'NUEVO DUEÑO' : 'EDITAR DUEÑO' }}</small><h3>{{ editOwnerForm.name || (editingOwnerIndex() === null ? 'Registrar dueño' : 'Dueño') }}</h3></div>
          <button type="button" class="modal-close" aria-label="Cerrar" (click)="closeOwnerModal()"><i class="bi bi-x-lg"></i></button>
        </header>
        <div class="modal-body">
          <label class="field-block">Nombre completo <em class="req">*</em><input type="text" id="owner-name-input" maxlength="120" [(ngModel)]="editOwnerForm.name" placeholder="Nombre del dueño" [class.invalid]="ownerNameInvalid()" /></label>
          <label class="field-block">Correo electrónico <em class="req">*</em><input type="email" id="owner-email-input" maxlength="120" [(ngModel)]="editOwnerForm.email" placeholder="correo@tusala.com" [class.invalid]="ownerEmailInvalid()" /></label>
          <p *ngIf="ownerEditError()" class="contacts-error" role="alert">{{ ownerEditError() }}</p>
        </div>
        <footer class="modal-actions">
          <button type="button" class="secondary-btn" (click)="closeOwnerModal()">Cancelar</button>
          <button type="button" class="action-btn" (click)="saveOwnerEdit()">{{ editingOwnerIndex() === null ? 'Crear dueño' : 'Guardar cambios' }}</button>
        </footer>
      </section>
    </div>

    <!-- Modal quitar dueño -->
    <div *ngIf="removingOwnerIndex() !== null" class="modal-overlay" (click)="closeOwnerRemove()">
      <section class="modal-box owner-remove-modal" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
        <header class="modal-header">
          <span class="modal-icon danger"><i class="bi bi-person-x"></i></span>
          <div><small>QUITAR DUEÑO</small><h3>{{ owners()[removingOwnerIndex()!]?.name || 'Dueño' }}</h3></div>
          <button type="button" class="modal-close" aria-label="Cerrar" (click)="closeOwnerRemove()"><i class="bi bi-x-lg"></i></button>
        </header>
        <div class="modal-body"><p class="modal-intro">Esta persona dejará de recibir las notificaciones de suscripciones del sistema. Podrás volver a agregarla más tarde.</p></div>
        <footer class="modal-actions">
          <button type="button" class="secondary-btn" (click)="closeOwnerRemove()">Cancelar</button>
          <button type="button" class="danger-btn" (click)="confirmOwnerRemove()">Quitar dueño</button>
        </footer>
      </section>
    </div>

    <ng-template #loadingState>
      <div class="contacts-loading"><span class="spinner"></span><span>Cargando contactos de TuSala…</span></div>
    </ng-template>
  `,
  styles: [`
    :host{display:block;margin-top:1.25rem}
    .contacts-admin{display:grid;gap:1.1rem}
    .contacts-head{padding:1rem 1.1rem;border:1px solid var(--ts-border-strong);border-radius:14px;background:linear-gradient(145deg, var(--ts-surface-2), var(--ts-surface-3));box-shadow:var(--ts-shadow-sm)}
    .contacts-head div{max-width:820px}
    .eyebrow{display:block;color:var(--ts-text-subtle);font-size:.62rem;font-weight:800;letter-spacing:.09em;text-transform:uppercase}
    .contacts-head h3{margin:.25rem 0 .3rem;color:var(--ts-text);font-size:1.18rem}
    .contacts-head p{margin:0;color:var(--ts-text-muted);font-size:.82rem}
    .contacts-card{padding:1.05rem 1.1rem;border:1px solid var(--ts-border-strong);border-radius:14px;background:linear-gradient(160deg,var(--ts-surface),var(--ts-bg-elevated));transition:border-color var(--ts-transition),box-shadow var(--ts-transition)}
    .contacts-card.invalid{border-color:var(--ts-danger);box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-danger) 20%, transparent)}
    .contacts-card header{display:flex;align-items:center;gap:.7rem;margin-bottom:.85rem}
    .contacts-card header i{display:grid;place-items:center;width:34px;height:34px;flex:0 0 34px;border-radius:9px;color:var(--ts-primary);background:color-mix(in srgb,var(--ts-primary) 15%, transparent);font-size:1rem}
    .contacts-card header small{display:block;color:var(--ts-text-subtle);font-size:.58rem;font-weight:800;letter-spacing:.09em;text-transform:uppercase}
    .contacts-card header strong{color:var(--ts-text);font-size:.92rem}
    .contacts-emails .contacts-email-fields{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:.2rem 1rem}
    .field-block{display:grid;gap:.32rem}
    .field-block>span{color:var(--ts-text-muted);font-size:.72rem;font-weight:700}
    .field-block .req{color:var(--ts-danger);font-style:normal}
    .field-hint{color:var(--ts-text-subtle);font-size:.66rem}
    .field-block input{box-sizing:border-box;width:100%;height:38px;padding:0 .7rem;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:9px;background:var(--ts-bg-elevated);font-size:.84rem;outline:none;transition:border-color var(--ts-transition),box-shadow var(--ts-transition)}
    .field-block input:focus{border-color:var(--ts-primary);box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-primary) 22%, transparent)}
    .field-block input.invalid{border-color:var(--ts-danger);box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-danger) 20%, transparent)}
    .field-block input::placeholder{color:var(--ts-text-subtle)}
    .contacts-directory .add-chip,.contacts-phones .add-chip{margin-left:auto}
    .owner-directory{display:grid;gap:.7rem;grid-template-columns:1fr}
    .owner-empty{display:grid;gap:.2rem;padding:1.1rem;text-align:center;color:var(--ts-text-muted);border:1px dashed var(--ts-border-strong);border-radius:12px;grid-column:1/-1}
    .owner-empty i{font-size:1.4rem;color:var(--ts-primary);margin-bottom:.3rem}
    .contacts-directory app-management-record-card[data-has-account="true"] .record-status{color:var(--ts-success)}
    .account-state-ok{color:var(--ts-success);font-weight:700}
    .account-state-off{color:var(--ts-text-subtle)}
    .phone-list{display:grid;gap:.55rem;margin-bottom:.7rem}
    .phone-row{display:flex;align-items:center;gap:.6rem;padding:.5rem .7rem;border:1px solid var(--ts-border);border-radius:11px;background:var(--ts-bg-elevated)}
    .phone-row>i{color:var(--ts-primary);font-size:1rem}
    .phone-row input{flex:1;height:36px;padding:0 .7rem;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:9px;background:var(--ts-bg-elevated);font-size:.84rem;outline:none}
    .phone-row input.invalid{border-color:var(--ts-danger)}
    .phone-row input:focus{border-color:var(--ts-primary)}
    .icon-action{display:grid;place-items:center;width:34px;height:34px;flex:0 0 34px;border:1px solid var(--ts-border-strong);border-radius:9px;color:var(--ts-text-muted);background:color-mix(in srgb,var(--ts-primary) 8%, transparent);cursor:pointer;transition:background var(--ts-transition),color var(--ts-transition),border-color var(--ts-transition)}
    .icon-action:hover{background:color-mix(in srgb,var(--ts-primary) 18%, transparent);color:var(--ts-text)}
    .icon-action.danger{color:var(--ts-danger);border-color:color-mix(in srgb,var(--ts-danger) 40%, transparent);background:color-mix(in srgb,var(--ts-danger) 12%, transparent)}
    .icon-action.danger:hover{background:color-mix(in srgb,var(--ts-danger) 24%, transparent);color:var(--ts-text)}
    .add-chip{display:inline-flex;align-items:center;gap:.4rem;justify-self:start;padding:.5rem .85rem;color:var(--ts-text);border:1px dashed color-mix(in srgb,var(--ts-primary) 45%, transparent);border-radius:9px;background:transparent;font-size:.78rem;cursor:pointer}
    .add-chip:hover{border-color:var(--ts-primary);background:color-mix(in srgb,var(--ts-primary) 12%, transparent)}
    .contacts-error{padding:.55rem .8rem;color:var(--ts-danger);border:1px solid color-mix(in srgb,var(--ts-danger) 45%, transparent);border-radius:10px;background:color-mix(in srgb,var(--ts-danger) 12%, transparent);font-size:.8rem;margin-top:.6rem}
    .contacts-actions{display:flex;justify-content:flex-end;gap:.7rem;margin-top:.3rem}
    .contacts-loading{display:flex;align-items:center;gap:.7rem;padding:2rem;color:var(--ts-text-muted);font-size:.9rem}
    .spinner{width:20px;height:20px;border:3px solid color-mix(in srgb,var(--ts-primary) 30%, transparent);border-top-color:var(--ts-primary);border-radius:50%;animation:spin .8s linear infinite}
    @keyframes spin{to{transform:rotate(360deg)}}
    .modal-box{width:min(460px,100%);max-width:100%;box-sizing:border-box;padding:0;overflow-x:hidden !important;overflow-y:auto;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:16px;background:linear-gradient(160deg,var(--ts-surface),var(--ts-bg-elevated));box-shadow:var(--ts-shadow-floating)}
    .modal-header{display:flex;align-items:center;gap:.7rem;min-width:0;padding:1rem 1.1rem;border-bottom:1px solid var(--ts-border);background:linear-gradient(180deg,color-mix(in srgb,var(--ts-primary) 12%, transparent),transparent)}
    .modal-header .modal-icon{display:grid;place-items:center;flex:0 0 38px;width:38px;height:38px;border-radius:10px;color:var(--ts-primary);background:color-mix(in srgb,var(--ts-primary) 16%, transparent);font-size:1.05rem}
    .modal-header .modal-icon.danger{color:var(--ts-danger);background:color-mix(in srgb,var(--ts-danger) 16%, transparent)}
    .modal-header>div{min-width:0;flex:1 1 auto}
    .modal-header small{display:block;color:var(--ts-text-subtle);font-size:.58rem;font-weight:800;letter-spacing:.09em;text-transform:uppercase}
    .modal-header h3{margin:0;color:var(--ts-text);font-size:1rem;overflow-wrap:anywhere}
    .modal-close{flex:0 0 34px;margin-left:auto;display:grid;place-items:center;width:34px;height:34px;color:var(--ts-text-muted);border:1px solid transparent;border-radius:9px;background:transparent;cursor:pointer}
    .modal-close:hover{color:var(--ts-text);background:var(--ts-surface-hover)}
    .modal-body{box-sizing:border-box;padding:1.1rem}
    .modal-body .field-block{margin-bottom:.8rem}
    .modal-intro{margin:0;color:var(--ts-text-muted);font-size:.84rem}
    .modal-actions{display:flex;justify-content:flex-end;gap:.7rem;padding:.9rem 1.1rem;border-top:1px solid var(--ts-border)}
    @media(max-width:780px){
      .contacts-head{padding:.9rem 1rem}
      .contacts-card{padding:.9rem}
      .contacts-card header{flex-wrap:wrap}
      .contacts-directory .add-chip,.contacts-phones .add-chip{margin-left:0}
    }
    @media(max-width:520px){
      :host{margin-top:.75rem}
      .contacts-admin{gap:.85rem}
      .contacts-head h3{font-size:1.05rem}
      .contacts-emails .contacts-email-fields{grid-template-columns:1fr}
      .phone-row{flex-wrap:wrap}
      .contacts-actions{flex-direction:column-reverse}
      .contacts-actions button{width:100%}
      .modal-actions{flex-direction:column-reverse}
      .modal-actions button{width:100%}
    }
  `]
})
export class PlatformContactsAdminComponent implements OnInit {
  readonly supportEmail = signal('');
  readonly administrationEmail = signal('');
  readonly senderEmail = signal('');
  readonly owners = signal<PlatformOwner[]>([]);
  readonly contactPhones = signal<string[]>([]);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly emailsError = signal('');
  readonly ownersError = signal('');
  readonly phonesError = signal('');

  readonly ownerModalOpen = signal(false);
  readonly editingOwnerIndex = signal<number | null>(null);
  readonly editOwnerForm: { name: string; email: string } = { name: '', email: '' };
  readonly ownerEditError = signal('');
  readonly removingOwnerIndex = signal<number | null>(null);

  private readonly emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  constructor(private svc: PlatformContactsService, private notifications: NotificationService, private dialogs: DialogService) {}

  ngOnInit(): void {
    this.svc.load().subscribe({
      next: d => { this.hydrate(d); this.loading.set(false); },
      error: () => {
        this.loading.set(false);
        this.emailsError.set('No se pudieron cargar los contactos de la plataforma.');
        this.notifications.error('No se pudieron cargar los contactos de la plataforma.');
      }
    });
  }

  emailInvalid(value: string): boolean {
    if (!this.emailsError()) return false;
    const trimmed = value.trim();
    return !trimmed || !this.emailRe.test(trimmed);
  }

  ownerNameInvalid(): boolean {
    return !!this.ownerEditError() && !this.editOwnerForm.name.trim();
  }

  ownerEmailInvalid(): boolean {
    if (!this.ownerEditError()) return false;
    const trimmed = this.editOwnerForm.email.trim();
    return !trimmed || !this.emailRe.test(trimmed);
  }

  private scrollToField(id: string): void {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (el instanceof HTMLInputElement) el.focus({ preventScroll: true });
    });
  }

  setPhone(i: number, value: string): void {
    this.contactPhones.update(list => list.map((p, idx) => (idx === i ? value : p)));
  }

  addPhone(): void {
    this.contactPhones.update(list => [...list, '']);
  }

  removePhone(i: number): void {
    this.contactPhones.update(list => list.filter((_, idx) => idx !== i));
  }

  openOwnerCreate(): void {
    this.editOwnerForm.name = '';
    this.editOwnerForm.email = '';
    this.editingOwnerIndex.set(null);
    this.ownerEditError.set('');
    this.ownerModalOpen.set(true);
  }

  openOwnerEdit(i: number): void {
    const owner = this.owners()[i];
    this.editOwnerForm.name = owner?.name || '';
    this.editOwnerForm.email = owner?.email || '';
    this.editingOwnerIndex.set(i);
    this.ownerEditError.set('');
    this.ownerModalOpen.set(true);
  }

  closeOwnerModal(): void {
    this.ownerModalOpen.set(false);
  }

  saveOwnerEdit(): void {
    if (!this.editOwnerForm.name.trim()) {
      this.ownerEditError.set('El nombre del dueño es obligatorio.');
      this.notifications.error('El nombre del dueño es obligatorio.');
      this.scrollToField('owner-name-input');
      return;
    }
    const email = this.editOwnerForm.email.trim();
    if (!email || !this.emailRe.test(email)) {
      this.ownerEditError.set('El correo del dueño es obligatorio y debe tener un formato válido.');
      this.notifications.error('El correo del dueño es obligatorio y debe tener un formato válido.');
      this.scrollToField('owner-email-input');
      return;
    }
    const index = this.editingOwnerIndex();
    if (index === null) {
      this.owners.update(list => [...list, { ...this.editOwnerForm }]);
    } else {
      this.owners.update(list => list.map((o, idx) => (idx === index ? { ...this.editOwnerForm } : o)));
    }
    this.closeOwnerModal();
  }

  openOwnerRemove(i: number): void {
    this.removingOwnerIndex.set(i);
  }

  closeOwnerRemove(): void {
    this.removingOwnerIndex.set(null);
  }

  confirmOwnerRemove(): void {
    const i = this.removingOwnerIndex();
    if (i !== null) this.owners.update(list => list.filter((_, idx) => idx !== i));
    this.closeOwnerRemove();
  }

  discard(): void {
    if (this.snapshot) this.hydrate(this.snapshot);
    this.emailsError.set('');
    this.ownersError.set('');
    this.phonesError.set('');
  }

  private fail(group: 'emails' | 'owners' | 'phones', message: string, scrollId: string): void {
    if (group === 'emails') this.emailsError.set(message);
    if (group === 'owners') this.ownersError.set(message);
    if (group === 'phones') this.phonesError.set(message);
    this.notifications.error(message);
    this.scrollToField(scrollId);
  }

  async save(): Promise<void> {
    const support = this.supportEmail().trim();
    const administration = this.administrationEmail().trim();
    const sender = this.senderEmail().trim();

    if (!support || !this.emailRe.test(support)) {
      this.fail('emails', 'El correo de soporte es obligatorio y debe tener un formato válido.', 'support-email-input');
      return;
    }
    if (!administration || !this.emailRe.test(administration)) {
      this.fail('emails', 'El correo de administración es obligatorio y debe tener un formato válido.', 'administration-email-input');
      return;
    }
    if (!sender || !this.emailRe.test(sender)) {
      this.fail('emails', 'El correo remitente del sistema es obligatorio y debe tener un formato válido.', 'sender-email-input');
      return;
    }

    const ownersClean = this.owners().filter(o => o.name.trim() || o.email.trim());
    if (ownersClean.length === 0) {
      this.fail('owners', 'Debe registrar al menos un dueño de TuSala.', 'owners-section');
      return;
    }
    for (const o of ownersClean) {
      if (!o.name.trim()) {
        this.fail('owners', 'Cada dueño registrado debe tener un nombre.', 'owners-section');
        return;
      }
      if (!o.email.trim() || !this.emailRe.test(o.email.trim())) {
        this.fail('owners', `El correo de ${o.name} es obligatorio y debe tener un formato válido.`, 'owners-section');
        return;
      }
    }

    const phones = this.contactPhones().map(p => p.trim()).filter(Boolean);
    if (phones.length === 0) {
      this.fail('phones', 'Debe registrar al menos un teléfono de contacto de TuSala.', 'phones-section');
      return;
    }

    this.emailsError.set('');
    this.ownersError.set('');
    this.phonesError.set('');

    this.saving.set(true);
    try {
      const res = await this.dialogs.confirmAndRun(
        {
          title: 'Guardar contactos de la plataforma',
          message: 'Estos correos y dueños recibirán las notificaciones de negocios y suscripciones de todo el sistema. ¿Deseas guardar los cambios?',
          tone: 'info',
          confirmText: 'Guardar contactos',
        },
        () => this.svc.update({
          support_email: support,
          administration_email: administration,
          sender_email: sender,
          owners: ownersClean,
          contact_phones: phones
        }),
        'Contactos de TuSala actualizados correctamente.',
        err => friendlyError(err, 'No se pudo guardar la información de contacto.'),
      );
      if (res !== null) this.hydrate(res);
    } catch (err) {
      const msg = friendlyError(err, 'No se pudo guardar la información de contacto.');
      this.emailsError.set(msg);
      this.notifications.error(msg);
      this.scrollToField('emails-section');
    } finally {
      this.saving.set(false);
    }
  }

  private snapshot: {
    support_email: string;
    administration_email: string;
    sender_email: string;
    owners: PlatformOwner[];
    contact_phones: string[];
  } | null = null;

  private hydrate(d: {
    support_email: string;
    administration_email: string;
    sender_email: string;
    owners: PlatformOwner[];
    contact_phones: string[];
  }): void {
    this.snapshot = structuredClone(d);
    this.supportEmail.set(d.support_email || '');
    this.administrationEmail.set(d.administration_email || '');
    this.senderEmail.set(d.sender_email || '');
    this.owners.set(d.owners?.length ? d.owners.map(o => ({ ...o })) : [{ name: '', email: '', phone: '' }]);
    this.contactPhones.set(d.contact_phones?.length ? [...d.contact_phones] : ['']);
  }
}
